/**
 * Shared contact helpers for the marketing site
 * (location SEO pages, local business schema, hero CTAs).
 */

import { resolvePublicDisplayCompanyName } from './companyBranding.js';
import { COMPANY } from '../constants/index.js';
import { phoneTelHref } from './phoneTelHref.js';
import { substituteMarketingSiteLinksInText } from './marketingSiteLinks.js';

export const MARKETING_SITE_COMPANY_NAME = COMPANY.NAME;
export const MARKETING_SITE_PHONE = COMPANY.PHONE;

export function resolveMarketingSitePhone(phone?: string): string {
  return phone?.trim() || COMPANY.PHONE;
}

export function resolveMarketingSiteCompanyName(companyName?: string): string {
  return resolvePublicDisplayCompanyName(companyName);
}

/**
 * Whether marketing copy is being rendered for a tenant rather than for the
 * original Tools & Bubbles site. Legacy corpus copy carries that company's
 * location, tenure and rating, so tenant pages need generated copy instead of a
 * find-and-replace on the brand name.
 */
export function isTenantMarketingBrand(companyName?: string): boolean {
  const resolved = resolveMarketingSiteCompanyName(companyName);
  return Boolean(resolved) && resolved !== COMPANY.NAME;
}

/** Phone digits for SEO titles (e.g. "9995216660"). */
export function marketingSitePhoneDisplay(phone?: string): string {
  const digits = resolveMarketingSitePhone(phone).replace(/\D/g, '');
  if (digits.startsWith('91') && digits.length >= 12) {
    return digits.slice(2);
  }
  return digits;
}

/** Example local number for phone inputs (tenant-aware, no hard-coded T&B digits). */
export function marketingSitePhoneInputPlaceholder(phone?: string): string {
  const trimmed = phone?.trim();
  if (trimmed) {
    return marketingSitePhoneDisplay(trimmed);
  }
  return '9876543210';
}

/** E.164 telephone for schema.org JSON-LD. */
export function schemaOrgTelephoneFromPhone(phone?: string): string {
  const href = phoneTelHref(resolveMarketingSitePhone(phone));
  return href.replace(/^tel:/, '');
}

/** Replace legacy hardcoded T&B company name in marketing copy. */
export function substituteMarketingCompanyNameInText(text: string, companyName?: string): string {
  if (!text) return text;
  const resolved = resolveMarketingSiteCompanyName(companyName);
  if (!resolved || resolved === COMPANY.NAME) return text;
  return text.replace(/Tools\s*&\s*Bubbles/gi, resolved).replace(/Tools\s+and\s+Bubbles/gi, resolved);
}

export type MarketingBrandingSubstituteOptions = {
  phone?: string;
  email?: string;
  companyName?: string;
  /** Tenant marketing site origin; rewrites legacy toolsandbubbles.com links in corpus copy. */
  websiteUrl?: string;
};

/** Replace legacy T&B phone, email, company name, and site links in marketing copy. */
export function substituteMarketingBrandingInText(
  text: string,
  options?: MarketingBrandingSubstituteOptions,
): string {
  const withContact = substituteMarketingContactInText(text, options?.phone, options?.email);
  const withCompany = substituteMarketingCompanyNameInText(withContact, options?.companyName);
  return substituteMarketingSiteLinksInText(withCompany, options?.websiteUrl);
}

/** Replace legacy hardcoded T&B phone and email strings in marketing copy. */
export function substituteMarketingContactInText(text: string, phone?: string, email?: string): string {
  if (!text) return text;
  const display = marketingSitePhoneDisplay(phone);
  const resolved = resolveMarketingSitePhone(phone);
  const formatted = resolved.startsWith('+91') ? `+91 ${display}` : resolved;
  let result = text
    .replace(/\+91[\s-]*9995216660/g, formatted)
    .replace(/\+91-9995216660/g, formatted)
    .replace(/9995216660/g, display);
  const resolvedEmail = email?.trim();
  if (resolvedEmail) {
    result = result.replace(/customercare@toolsandbubbles\.com/gi, resolvedEmail);
  }
  return result;
}
